import { faCheck } from "@fortawesome/pro-regular-svg-icons";
import { Button, Icon, Message } from "@intility/bifrost-react";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useAddClientToPlatform } from "../api/platforms";
import type { AuthMethod, ClientListItem } from "../api/types";
import { APP_TYPE_NAME_SUFFIX } from "../lib/clientTypes";
import { ClientTypeBadge } from "./ClientTypeBadge";
import { NewClientCredentialQuestion } from "./NewClientCredentialQuestion";
import "./ClientObject.css";
import "./NewClientForm.css";

/** The name the API gets: the platform's name and the type ("Apm API"). */
export function apiClientName(platformName: string): string {
  return `${platformName} ${APP_TYPE_NAME_SUFFIX.api}`;
}

// The API that "Auto create" leaves to fill in, in the box a client is shown
// in: type and purpose, the name, and where the workload runs. The API does
// not exist until Save changes; nothing is assumed about where it runs, so
// saving waits for an answer.
export function NewApiClientForm({
  platformId,
  platformName,
  onCreated,
}: {
  platformId: string;
  platformName: string;
  /** Called with the API once it exists in the platform. */
  onCreated: (client: ClientListItem) => void;
}) {
  const addClient = useAddClientToPlatform(platformId);
  const queryClient = useQueryClient();
  // From pressing Save until the platform has loaded with the new client.
  const [saving, setSaving] = useState(false);
  const [authMethod, setAuthMethod] = useState<AuthMethod | undefined>();

  async function save() {
    if (!authMethod) return;
    setSaving(true);
    try {
      const created = await addClient.mutateAsync({
        name: apiClientName(platformName),
        appType: "api",
        purposeTag: "api",
        authMethod,
      });
      // Wait for the platform to load again, so the new client is in the menu
      // by the time it is selected.
      await queryClient.invalidateQueries({
        queryKey: ["platforms", platformId],
      });
      onCreated(created);
    } catch {
      // The error shows from the mutation state.
      setSaving(false);
    }
  }

  const canSave = !!authMethod && !saving;

  return (
    <div className="client-object client-object-fill new-client-form">
      <div>
        <div style={{ marginBottom: 16 }}>
          <ClientTypeBadge appType="api" purposeTag="api" />
        </div>
        <h3 className="bf-h3" style={{ margin: 0 }}>
          {apiClientName(platformName)}
        </h3>
      </div>

      <NewClientCredentialQuestion
        name="new-api-client-credential"
        value={authMethod}
        onChange={setAuthMethod}
      />

      {addClient.error && (
        <Message state="alert" header="Could not create the client">
          {addClient.error instanceof Error
            ? addClient.error.message
            : "Something went wrong."}
        </Message>
      )}

      <div className="new-client-footer new-client-footer-end">
        <Button
          variant="filled"
          onClick={canSave ? save : undefined}
          state={canSave ? undefined : "inactive"}
        >
          <Icon icon={faCheck} marginRight />
          {saving ? "Saving…" : "Save changes"}
        </Button>
      </div>
    </div>
  );
}
